import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Layout, ProgressBar, TextStyle } from "@shopify/polaris";
import { useApolloClient, useQuery } from "@apollo/react-hooks";

import Moment from "moment";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";

import constant from "lib/constant/constant";
import { baseHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";
import { Banner, Spinner } from "lib/components";

import { GET_ORDER_LINES, GET_ORDER_EXPORT_SETTING, GET_SELLER_DEFAULT_ADDRESS } from "app/orders/apollo/queries";
import { GET_WHOLESALE_SETTING } from "app/product/apollo/queries";

import { useBatchExport } from "lib/hooks";
import orderHelper from "app/orders/modules/orderHelper";

const ExportOrder = () => {
  const data = useContext(PrivateContext);
  const { cms, currentUser } = data;
  const client = useApolloClient();
  const hasStartedRef = useRef(false);
  const isBulkFulfillRef = useRef(false);
  const isVendor = baseHelper.isVendor(currentUser);
  const hideValue = {
    label: "",
    isHideCsv: true,
    isHidePdf: true,
  };

  const { loading: addressLoading, data: addressData } = useQuery(GET_SELLER_DEFAULT_ADDRESS);
  const { data: getOrderSettingData, loading: settingLoading, error: settingError } = useQuery(
    GET_ORDER_EXPORT_SETTING
  );
  const { loading: wholesaleLoading, data: wholesaleData } = useQuery(GET_WHOLESALE_SETTING);

  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });

  const showErrorBanner = useCallback((title) => {
    setBanner({ title, status: constant.CRITICAL, isOpen: true });
    setTimeout(() => window.close(), 2000);
  }, []);

  const fetchPage = useCallback(
    (page, filters) =>
      client
        .query({
          query: GET_ORDER_LINES,
          variables: { input: { ...filters, page } },
          fetchPolicy: "network-only",
        })
        .then((res) => {
          const response = res.data.getOrderLines;
          if (response.status !== constant.OK) {
            throw new Error(response.error || cms("common.message.error.noOrder"));
          }
          const { orderLines = [], hasMore = false, totalCount } = response.data || {};
          return { rows: orderLines, hasMore, totalCount };
        }),
    [client, cms]
  );

  const mapRows = useCallback(
    (rows) => {
      if (isBulkFulfillRef.current) {
        return orderHelper.orderFulfillCSVData(rows, cms);
      }
      const settingResponse = baseHelper.getResponseData(getOrderSettingData, constant.gql.GET_ORDER_EXPORT_SETTING);
      const { isMarkupDiscountExport = false } = settingResponse;
      if (!isVendor && settingResponse && settingResponse.export) {
        settingResponse.export.discount = hideValue;
      }
      if (isVendor && !isMarkupDiscountExport && settingResponse && settingResponse.export) {
        settingResponse.export.markupPrice = hideValue;
        settingResponse.export.discount = hideValue;
      }
      const wholesaleResponse = baseHelper.getResponseData(wholesaleData, constant.gql.GET_WHOLESALE_SETTING);
      const addressResponse = baseHelper.getResponseData(addressData, constant.gql.GET_SELLER_DEFAULT_ADDRESS);
      const { isWholesalePriceVisible = false } = wholesaleResponse;
      return orderHelper.orderCSVData(
        rows,
        settingResponse,
        isWholesalePriceVisible,
        true,
        false,
        isVendor,
        cms,
        addressResponse
      );
    },
    [getOrderSettingData, wholesaleData, addressData, isVendor, cms]
  );

  const getFileName = useCallback(() => {
    const date = Moment(new Date(), "DD-MM-YYYY").format("LL");
    return `Order Export - ${date}.csv`;
  }, []);

  const onError = useCallback((err) => showErrorBanner(err.message || cms("common.message.error.noOrder")), [
    showErrorBanner,
    cms,
  ]);

  const { status: exportStatus, fetchedCount, totalCount, progress, start } = useBatchExport({
    fetchPage,
    mapRows,
    getFileName,
    onError,
  });

  useEffect(() => {
    if (exportStatus === "empty") {
      showErrorBanner(cms("common.message.error.noOrder"));
    }
    if (exportStatus === "done") {
      setTimeout(() => window.close(), 500);
    }
  }, [exportStatus, showErrorBanner, cms]);

  useEffect(() => {
    const settingsLoading = settingLoading || wholesaleLoading || addressLoading;
    if (settingsLoading || hasStartedRef.current) {
      return;
    }
    if (settingError) {
      showErrorBanner(settingError.message || cms("common.message.error.noOrder"));
      return;
    }

    hasStartedRef.current = true;
    const {
      status,
      fulfillmentStatus,
      vendor,
      accountingStatus,
      bulkFulfill = false,
    } = baseHelper.queryParamsFromLocation(data);
    const filters = {};
    if (status && status.length) {
      filters.status = status.split(",");
    }
    if (fulfillmentStatus && fulfillmentStatus.length) {
      filters.fulfillmentStatus = fulfillmentStatus.split(",");
    }
    if (vendor && vendor.length) {
      filters.vendor = vendor.split(",");
    }
    if (accountingStatus && accountingStatus.length) {
      filters.accountingStatus = accountingStatus.split(",");
    }
    if (bulkFulfill) {
      filters.isCampaign = true;
    }
    isBulkFulfillRef.current = bulkFulfill;
    start(filters);
  }, [data, start, cms, showErrorBanner, settingLoading, wholesaleLoading, addressLoading, settingError]);

  return (
    <Layout.Section>
      {(addressLoading || wholesaleLoading) && <Spinner isFullPage />}
      {exportStatus === "loading" && (
        <div className="fullPageProgress">
          <ProgressBar progress={progress} size="medium" />
          <br />
          <TextStyle variation="subdued">
            {totalCount ? `Exporting ${fetchedCount} of ${totalCount} records...` : "Preparing export..."}
          </TextStyle>
        </div>
      )}
      {banner.isOpen && (
        <Banner
          title={banner.title}
          status={banner.status}
          isOpen={banner.isOpen}
          onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
        />
      )}
    </Layout.Section>
  );
};

export default withFeature(withErrorBoundary(ExportOrder), { feature: constant.EXPORT_ORDER });
